"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useCart } from "../contexts/CartContext";
import { setStoredStoreId } from "@/lib/store-storage";
import { deleteCookie, getCookie, setCookie } from "@/lib/cookies";
import MarkIcon from "../icons/MarkIcon";
import StorefrontIcon from "../icons/StorefrontIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import Image from "next/image";
import Button from "../ui/Button";
import ModalPortal from "../ui/ModalPortal";
import { useScrollLock } from "@/lib/use-scroll-lock";
import FulfillmentToggle from "@/components/layout/FulfillmentToggle";

// ─── Button style constants ────────────────────────────────────────────────────

const SEGMENT_BTN_BASE =
  "h-auto flex-1 rounded-lg border-0 py-3 text-body-16 font-medium transition-colors focus:outline-none";
const SEGMENT_BTN_ACTIVE =
  "!bg-blue-80 !text-constant-white hover:!bg-blue-100 hover:!text-constant-white active:!bg-blue-100 active:!text-constant-white";
const SEGMENT_BTN_INACTIVE =
  "bg-transparent text-text-secondary hover:bg-bg-1";

const STORE_OPTION_BTN =
  "h-auto w-full text-left px-4 py-3 text-text-14 transition-colors flex items-center justify-between border-0 border-b border-neutral-10 last:border-b-0 focus:outline-none";
const STORE_OPTION_ACTIVE =
  "!bg-blue-80 !text-constant-white font-medium hover:!bg-blue-100 hover:!text-constant-white active:!bg-blue-100 active:!text-constant-white";
const STORE_OPTION_INACTIVE =
  "text-text-primary hover:bg-bg-1";

type Store = {
  covaLocationId: number;
  name: string;
  city?: string;
  province?: string;
  address?: string;
};

type EntryModalProps = {
  stores: Store[];
  base: string;
  onClose: () => void;
};

const STORAGE_KEY = "dank_entry_preferences";
const STORAGE_EXPIRY_DAYS = 30;

interface StoredPreferences {
  ageVerified: boolean;
  shoppingMethod: "pickup" | "delivery";
  selectedLocationId?: number;
  postalCode?: string;
  expiryDate: number;
}

export default function EntryModal({ stores, base, onClose }: EntryModalProps) {
  const router = useRouter();
  const { setStore, setFulfillmentMode } = useCart();

  // Section 1: Age Verification
  const [ageVerified, setAgeVerified] = useState<boolean | null>(null);

  // Section 2: Shopping Method
  const [shoppingMethod, setShoppingMethod] = useState<"pickup" | "delivery">(
    "delivery"
  );

  // Section 3: Location Selection
  const [postalCode, setPostalCode] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null
  );
  const [postalError, setPostalError] = useState("");
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showStoresDropdown, setShowStoresDropdown] = useState(false);

  // Remember Me is always-on (persist selection by default)
  const rememberMe = true;

  // Check if preferences are stored and auto-apply them
  useEffect(() => {
    // Persist age denial across same-session page refreshes
    try {
      if (sessionStorage.getItem("dank_age_denied") === "1") {
        setAgeVerified(false);
        return;
      }
    } catch {}

    const storedCookie = getCookie(STORAGE_KEY);
    const stored =
      storedCookie ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(STORAGE_KEY)
        : null);
    if (stored) {
      try {
        const prefs: StoredPreferences = JSON.parse(stored);
        // Check if preferences are still valid (not expired)
        if (prefs.expiryDate && Date.now() < prefs.expiryDate) {
          setAgeVerified(prefs.ageVerified);
          setShoppingMethod(prefs.shoppingMethod);
          // RememberMe is always-on

          if (prefs.selectedLocationId) {
            setSelectedLocationId(prefs.selectedLocationId);
          }
          if (prefs.postalCode) {
            setPostalCode(prefs.postalCode);
            // The auto-resolve useEffect will handle postal code resolution
          }

          // If age verified and we have location, auto-proceed
          if (prefs.ageVerified && prefs.selectedLocationId) {
            setTimeout(() => {
              setStore(prefs.selectedLocationId!);
              setFulfillmentMode(prefs.shoppingMethod);
              setStoredStoreId(prefs.selectedLocationId!);
              onClose();
            }, 300);
            // One-time migration cleanup
            if (!storedCookie) {
              setCookie(STORAGE_KEY, JSON.stringify(prefs), {
                maxAgeDays: STORAGE_EXPIRY_DAYS,
              });
              try {
                localStorage.removeItem(STORAGE_KEY);
              } catch {}
            }
            return;
          }
          // One-time migration cleanup
          if (!storedCookie) {
            setCookie(STORAGE_KEY, JSON.stringify(prefs), {
              maxAgeDays: STORAGE_EXPIRY_DAYS,
            });
            try {
              localStorage.removeItem(STORAGE_KEY);
            } catch {}
          }
        } else {
          // Expired, remove from storage (cookie + legacy localStorage)
          deleteCookie(STORAGE_KEY);
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch {}
        }
      } catch (e) {
        // Invalid storage, remove it
        deleteCookie(STORAGE_KEY);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save preferences to cookie (always-on)
  const savePreferences = () => {
    if (rememberMe && ageVerified) {
      const prefs: StoredPreferences = {
        ageVerified: true,
        shoppingMethod,
        expiryDate: Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
      };

      if (shoppingMethod === "pickup" && selectedLocationId) {
        prefs.selectedLocationId = selectedLocationId;
      } else if (shoppingMethod === "delivery") {
        if (postalCode) {
          prefs.postalCode = postalCode;
        }
        // Also save the resolved location ID if available
        if (selectedLocationId) {
          prefs.selectedLocationId = selectedLocationId;
        }
      }

      setCookie(STORAGE_KEY, JSON.stringify(prefs), {
        maxAgeDays: STORAGE_EXPIRY_DAYS,
      });
      // Cleanup legacy localStorage key
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    } else {
      deleteCookie(STORAGE_KEY);
      // Cleanup legacy localStorage key
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  };

  // Format postal code input
  const formatPostalCode = (value: string) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    } else {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
    }
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPostalCode(e.target.value);
    setPostalCode(formatted);
    setPostalError("");
  };

  const handlePostalCodeSubmit = async (postalValue?: string) => {
    const postal = (postalValue || postalCode)
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");

    if (!postal) {
      setPostalError("Please enter a postal code");
      return;
    }

    if (!/^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/.test(postal)) {
      setPostalError("Please enter a valid Canadian postal code");
      return;
    }

    setLoading(true);
    setPostalError("");

    try {
      const res = await fetch(
        `${base}/api/locations/resolve?postal=${encodeURIComponent(postal)}`
      );
      const data = await res.json();

      if (data?.success && data?.resolved?.covaLocationId) {
        const locationId = data.resolved.covaLocationId;
        setSelectedLocationId(locationId);
        setPostalError("");
      } else {
        setPostalError(
          data?.message ||
            "No store found for that postal code. Please try another location."
        );
      }
    } catch (err) {
      setPostalError("Unable to find store. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseMyLocation = async () => {
    if (!navigator.geolocation) {
      setPostalError("Geolocation is not supported by your browser.");
      return;
    }

    setLocationLoading(true);
    setPostalError("");

    try {
      // Get user's current position
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        }
      );

      const { latitude, longitude } = position.coords;

      // Use backend endpoint for reverse geocoding (avoids CORS and rate limiting issues)
      const reverseGeocodeUrl = `${base}/api/locations/reverse-geocode?lat=${latitude}&lon=${longitude}`;

      const geocodeRes = await fetch(reverseGeocodeUrl);

      if (!geocodeRes.ok) {
        throw new Error("Failed to get location details");
      }

      const geocodeData = await geocodeRes.json();

      if (!geocodeData.success || !geocodeData.postalCode) {
        setPostalError(
          geocodeData.error ||
            "Could not determine postal code from your location. Please enter it manually."
        );
        setLocationLoading(false);
        return;
      }

      // Format and set the postal code
      const formatted =
        geocodeData.formatted || formatPostalCode(geocodeData.postalCode);
      setPostalCode(formatted);

      // Now resolve the postal code to a store
      await handlePostalCodeSubmit(geocodeData.postalCode);
    } catch (error: unknown) {
      // Handle geolocation errors
      if (error instanceof GeolocationPositionError) {
        if (error.code === 1) {
          // PERMISSION_DENIED
          setPostalError(
            "Location access denied. Please enable location permissions or enter your postal code manually."
          );
        } else if (error.code === 2) {
          // POSITION_UNAVAILABLE
          setPostalError(
            "Location unavailable. Please enter your postal code manually."
          );
        } else if (error.code === 3) {
          // TIMEOUT
          setPostalError(
            "Location request timed out. Please try again or enter your postal code manually."
          );
        } else {
          setPostalError(
            "Unable to get your location. Please enter your postal code manually."
          );
        }
      } else if (error instanceof Error && error.message) {
        setPostalError(error.message);
      } else {
        setPostalError(
          "Unable to get your location. Please enter your postal code manually."
        );
      }
    } finally {
      setLocationLoading(false);
    }
  };

  const handleEnter = async () => {
    // Validate all required fields
    if (ageVerified === null) {
      return; // Age not verified
    }

    if (ageVerified === false) {
      // User said no to age verification - do nothing (button is disabled)
      return;
    }

    if (shoppingMethod === "pickup" && !selectedLocationId) {
      return; // Need to select a location
    }

    if (shoppingMethod === "delivery" && !postalCode.trim()) {
      return; // Need to enter postal code
    }

    if (shoppingMethod === "delivery" && !selectedLocationId) {
      // Need to resolve postal code first
      await handlePostalCodeSubmit();
      // If still no location after submit, don't proceed
      if (!selectedLocationId) {
        return;
      }
    }

    // Save preferences if remember me is checked
    savePreferences();

    // Set store and fulfillment mode
    if (selectedLocationId) {
      setStore(selectedLocationId);
      setFulfillmentMode(shoppingMethod);
      setStoredStoreId(selectedLocationId);

      onClose();
    }
  };

  // Check if Enter button should be enabled
  const isEnterEnabled = () => {
    // If user said "No" to age verification, disable Enter button
    if (ageVerified === false) return false;
    // If age not selected yet, disable
    if (ageVerified === null) return false;
    // If age verified is true, check other requirements
    if (shoppingMethod === "pickup" && !selectedLocationId) return false;
    if (shoppingMethod === "delivery") {
      if (!postalCode.trim()) return false;
      if (!selectedLocationId) return false;
    }
    return true;
  };

  // Auto-submit postal code when it's valid and delivery is selected (debounced)
  useEffect(() => {
    if (
      shoppingMethod === "delivery" &&
      postalCode.trim() &&
      !selectedLocationId &&
      !loading
    ) {
      const postal = postalCode.trim().toUpperCase().replace(/\s+/g, "");
      if (/^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/.test(postal)) {
        // Use a timeout to debounce the API call
        const timer = setTimeout(() => {
          handlePostalCodeSubmit(postal);
        }, 800);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, shoppingMethod]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.stores-dropdown-container')) {
        setShowStoresDropdown(false);
      }
    };

    if (showStoresDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showStoresDropdown]);

  useScrollLock(true);

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
      style={{
        backgroundColor: "#07063CBF",
        backdropFilter: "blur(25px)",
        WebkitBackdropFilter: "blur(25px)",
      }}
    >
      <div
        className={`relative w-full rounded-3xl bg-constant-white shadow-2xl ${
          ageVerified !== true ? "max-w-[360px]" : "max-w-[400px]"
        }`}
      >
        <div className="p-7">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Image src="/media/brand/logo.webp" width={175} height={90} alt="Dank" className="w-[175px] h-auto" priority fetchPriority="high" />
          </div>

          {/* Screen 1: Age Verification */}
          {ageVerified !== true ? (
            <div className="text-center">
              <h2 className="text-h3-desktop font-bold text-text-primary mb-2">
                Welcome to Dank
              </h2>
              <p className="text-text-secondary text-text-16 font-medium mb-6">
                Are you of legal age in your province?
              </p>

              {ageVerified === false && (
                <div className="mb-4 rounded-xl bg-red-50 border border-red-200 p-3">
                  <p className="text-sm text-red-700 font-medium">
                    Sorry, you must be of legal age to enter this site.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  variant="solid"
                  color="primary"
                  size="md"
                  className="w-full rounded-xl"
                  onClick={() => {
                    setAgeVerified(true);
                    try { sessionStorage.removeItem("dank_age_denied"); } catch {}
                    setSelectedLocationId(null);
                    setPostalCode("");
                    setPostalError("");
                  }}
                >
                  Yes, I&apos;m 18+
                </Button>

                <Button
                  variant="solid"
                  color="primary"
                  size="md"
                  onClick={() => {
                    setAgeVerified(false);
                    try { sessionStorage.setItem("dank_age_denied", "1"); } catch {}
                    setSelectedLocationId(null);
                    setPostalCode("");
                    setPostalError("");
                  }}
                  className="w-full rounded-xl"
                >
                  No
                </Button>
              </div>
            </div>
          ) : (
            /* Screen 2: Pickup / Delivery + Location */
            <div className="">
              {/* The header's control, not a second one that looks like it.
                  This was two Buttons with rounded-r-none / rounded-l-none, so
                  the halves were welded into one bar rather than a pill sitting
                  inside a track. */}
              <FulfillmentToggle
                className="mb-6 w-full"
                mode={shoppingMethod}
                onModeChange={(next) => {
                  setShoppingMethod(next);
                  setPostalCode("");
                  setSelectedLocationId(null);
                  setPostalError("");
                }}
              />

              {shoppingMethod === "pickup" ? (
                <div className="mb-4">
                  <div className="relative stores-dropdown-container">
                    <Button
                      raw
                      type="button"
                      onClick={() => setShowStoresDropdown(!showStoresDropdown)}
                      className="h-auto w-full flex items-center gap-3 rounded-xl border border-neutral-10 bg-constant-white px-4 py-3 text-left hover:bg-constant-white focus:outline-none"
                    >
                      <StorefrontIcon size={20} color="currentColor" className="w-5 h-5 text-neutral-70 flex-shrink-0" />
                      <span className="flex-1 text-body-16 font-medium text-text-secondary truncate min-w-0">
                        {selectedLocationId
                          ? stores.find((s) => s.covaLocationId === selectedLocationId)?.name ||
                            "Choose store"
                          : "Choose store"}
                      </span>
                      <ChevronDownIcon
                        size={16}
                        color="currentColor"
                        className={`w-4 h-4 text-neutral-70 transition-transform flex-shrink-0 ${
                          showStoresDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </Button>

                    {showStoresDropdown && (
                      <div className="absolute z-50 mt-2 w-full rounded-xl border border-neutral-10 bg-constant-white shadow-lg overflow-y-auto max-h-40">
                        {stores.map((store) => (
                          <Button
                            key={store.covaLocationId}
                            variant="ghost"
                            size="md"
                            onClick={() => {
                              setSelectedLocationId(store.covaLocationId);
                              setShowStoresDropdown(false);
                            }}
                            className={`${STORE_OPTION_BTN} ${selectedLocationId === store.covaLocationId ? STORE_OPTION_ACTIVE : STORE_OPTION_INACTIVE}`}
                          >
                            {`${store.name}${store.city ? `, ${store.city}` : ""}`}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  {/* Postal field with the geolocate action tucked inside it, so the
                      two ways of setting a location share one control instead of
                      the input sitting above a full-width button. */}
                  <div className="relative flex items-center rounded-xl border border-neutral-10 bg-constant-white transition-colors focus-within:border-blue-80 focus-within:ring-2 focus-within:ring-blue-80/20">
                    <input
                      type="text"
                      value={postalCode}
                      onChange={handlePostalCodeChange}
                      placeholder="Enter postal code"
                      maxLength={7}
                      autoComplete="postal-code"
                      aria-label="Postal code"
                      className="min-w-0 flex-1 bg-transparent py-3 pl-4 pr-1 text-body-16 font-medium text-text-primary placeholder:font-normal focus:outline-none"
                      disabled={loading || locationLoading}
                    />

                    {loading && (
                      <span className="mr-1 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-80 border-t-transparent" />
                    )}

                    <span className="mx-1 h-6 w-px shrink-0 bg-neutral-10" />

                    <Button
                      raw
                      type="button"
                      onClick={handleUseMyLocation}
                      disabled={loading || locationLoading}
                      aria-label="Use my current location"
                      title="Use my current location"
                      className="mr-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-blue-80 transition-colors hover:bg-blue-80/10 disabled:opacity-50"
                    >
                      {locationLoading ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <MarkIcon size={18} color="currentColor" className="h-[18px] w-[18px]" />
                      )}
                    </Button>
                  </div>

                  {(loading || locationLoading) && (
                    <p className="mt-2 text-center text-text-14 text-text-tertiary animate-pulse">
                      {locationLoading ? "Getting your location…" : "Finding your store…"}
                    </p>
                  )}

                  {!loading && selectedLocationId && !postalError && (
                    <div className="mt-2 flex items-center justify-center gap-1.5 text-text-14 text-green-700">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>
                        {stores.find(s => s.covaLocationId === selectedLocationId)?.name || "Store found"}
                      </span>
                    </div>
                  )}

                  {postalError && (
                    <p className="mt-2 text-text-14 text-red-60 text-center">
                      {postalError}
                    </p>
                  )}
                </div>
              )}

              <Button
                variant="solid"
                color="primary"
                size="lg"
                className="w-full rounded-xl"
                onClick={handleEnter}
                disabled={!isEnterEnabled()}
              >
                Continue
              </Button>

              {/*
                Picking a store by hand and letting the browser find one are two
                ways to answer the same question, so they read as alternatives.
                Delivery keeps its own location control inside the postal field,
                where it is filling that field in rather than choosing a shop.
              */}
              {shoppingMethod === "pickup" && (
                <>
                  <div className="my-4 flex items-center gap-3">
                    <span className="h-px flex-1 bg-neutral-10" aria-hidden />
                    <span className="text-text-14 font-medium text-text-tertiary">OR</span>
                    <span className="h-px flex-1 bg-neutral-10" aria-hidden />
                  </div>

                  <Button
                    variant="bordered"
                    size="lg"
                    className="w-full rounded-xl"
                    onClick={handleUseMyLocation}
                    disabled={locationLoading || loading}
                  >
                    <span className="flex items-center justify-center gap-2">
                      {locationLoading ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <MarkIcon size={18} color="currentColor" className="h-[18px] w-[18px]" />
                      )}
                      {locationLoading ? "Finding your store…" : "Use my location"}
                    </span>
                  </Button>

                  {/* Geolocation failures set postalError, which only the
                      delivery branch rendered, so a denied permission here
                      looked like nothing had happened. */}
                  {postalError && (
                    <p className="mt-2 text-center text-text-14 text-red-60">{postalError}</p>
                  )}
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
